import useSWR from 'swr';
import api from '../../services/api';
import type { SearchFolderPeriodPreset } from '../emails/utils/receivedDatePresets';

export type SearchInField = 'sender' | 'recipients' | 'subject' | 'body';

export type SearchRuleField = 'from' | 'to' | 'cc' | 'toOrCc' | 'fromToCcBcc' | 'subject' | 'body';
export type SearchRuleOperator = 'contains' | 'notContains' | 'is' | 'isNot' | 'beginsWith' | 'endsWith';
export type SearchFolderRulesMatch = 'all' | 'any' | 'allMessages';
export type { SearchFolderPeriodPreset };

export interface SearchRule {
  id: string;
  field: SearchRuleField;
  operator: SearchRuleOperator;
  value: string;
}

export type SearchFolderKind =
  | 'search'
  | 'inbox'
  | 'combined'
  | 'sent'
  | 'combinedSent'
  | 'mySent'
  | 'combinedMySent';

export function normalizeSearchFolderKind(kind?: string | null): SearchFolderKind {
  const k = (kind ?? 'search').trim().toLowerCase();
  if (k === 'inbox' || k === 'combined' || k === 'sent') return k;
  if (k === 'combinedsent') return 'combinedSent';
  if (k === 'mysent') return 'mySent';
  if (k === 'combinedmysent') return 'combinedMySent';
  return 'search';
}

export function searchFolderSentByMe(kind?: string | null): boolean {
  const normalized = normalizeSearchFolderKind(kind);
  return normalized === 'mySent' || normalized === 'combinedMySent';
}

export interface SearchFolder {
  id: number;
  userId: number;
  ownerName?: string;
  name: string;
  search?: string | null;
  searchIn?: SearchInField[] | null;
  rules?: SearchRule[] | null;
  rulesMatch?: SearchFolderRulesMatch | null;
  accountId?: number | null;
  accountIds?: number[] | null;
  folderKind?: SearchFolderKind | string | null;
  folder?: string | null;
  periodPreset?: SearchFolderPeriodPreset | string | null;
  periodYear?: number | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SearchFolderShare {
  shareId: number;
  sharedWithUserId: number;
  sharedWithUserName: string;
  permission: 'read' | 'write';
  createdAt: string;
}

export const useSearchFolders = () => {
  const { data, error, isLoading, mutate } = useSWR<SearchFolder[]>(
    '/emails/search-folders/manage',
    (url: string) => api.get(url).then((r) => r.data)
  );

  const create = async (body: {
    name: string;
    search?: string;
    searchIn?: SearchInField[];
    rules?: Array<{ field: SearchRuleField; operator: SearchRuleOperator; value: string }>;
    rulesMatch?: SearchFolderRulesMatch;
    accountId?: number;
    accountIds?: number[];
    folderKind?: SearchFolderKind;
    folder?: string;
    periodPreset?: SearchFolderPeriodPreset;
    periodYear?: number | null;
    displayOrder?: number;
  }) => {
    const created = await api.post<SearchFolder>('/emails/search-folders', body);
    await mutate();
    return created.data;
  };

  const update = async (
    id: number,
    body: Partial<{
      name: string;
      search: string;
      searchIn: SearchInField[];
      rules: Array<{ field: SearchRuleField; operator: SearchRuleOperator; value: string }>;
      rulesMatch: SearchFolderRulesMatch;
      accountId: number | null;
      accountIds?: number[] | null;
      folderKind?: SearchFolderKind | null;
      folder: string | null;
      periodPreset?: SearchFolderPeriodPreset | null;
      periodYear?: number | null;
      displayOrder: number;
    }>
  ) => {
    const updated = await api.patch<SearchFolder>(`/emails/search-folders/${id}`, body);
    await mutate();
    return updated.data;
  };

  const remove = async (id: number) => {
    await api.delete(`/emails/search-folders/${id}`);
    await mutate();
  };

  const shareFolder = async (
    folderId: number,
    body: { sharedWithUserId: number; permission?: 'read' | 'write' }
  ) => {
    await api.post(`/emails/search-folders/${folderId}/share`, body);
    await mutate();
  };

  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
    create,
    update,
    remove,
    shareFolder,
  };
};

export const useSearchFolder = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<SearchFolder>(
    id ? `/emails/search-folders/${id}` : null,
    (url: string) => api.get(url).then((r) => r.data)
  );
  return { data, isLoading, isError: error, mutate };
};

export const useSearchFolderShares = (folderId: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<SearchFolderShare[]>(
    folderId ? `/emails/search-folders/${folderId}/shares` : null,
    (url: string) => api.get(url).then((r) => r.data)
  );
  const revokeShare = async (shareId: number) => {
    await api.delete(`/emails/search-folders/shares/${shareId}`);
    await mutate();
  };
  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
    revokeShare,
  };
};
